/*
    Requires a child component to send and event up
    See mobile menu as a basic example
    Wll then have to add the listener in

    On rendering, also need to include the drawerHeader snippet which will then allow the drawer to close

*/

import { Component, Prop, Ref } from '@eastsideco/salvo-ts';
import { ThemeComponent } from '@/theme/ThemeComponent';
import { DrawerHeader } from './DrawerHeader';
import { BodyOverlay } from '../BodyOverlay/BodyOverlay';


import './DrawerContainer.scss';

@Component
export class DrawerContainer extends ThemeComponent {
    @Ref({ required: false }) drawerHeader!: DrawerHeader;
    @Ref({ required: false }) drawerContent!: HTMLElement;

    private focusableElements =
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    private previousFocus: Element = document.body;
    private bodyOverlay: BodyOverlay|null = null;

    /** Initialise component */
    init() {
        this.bodyOverlay = document.querySelector('.body-overlay');

        if (this.bodyOverlay) {
            this.$subscribe(this.bodyOverlay.onCloseChange, () => {
                this.close();
            });
        }

        this.setAttribute('aria-hidden', 'true');

        this.$subscribe(this.drawerHeader.onCloseChange, () => {
            this.close();
        });

        document.addEventListener('overlay-clicked', () => this.close());
    }

    /** Handle focus lock */
    handleFocusLock() {
        const firstFocusableElement = this.querySelectorAll(this.focusableElements)[0] as HTMLElement;
        const focusableContent = this.querySelectorAll(this.focusableElements);
        const lastFocusableElement = focusableContent[focusableContent.length - 1] as HTMLElement;

        document.addEventListener('keydown', (e) => {
            const isTabPressed = e.key === 'Tab' || e.keyCode === 9;

            if (!isTabPressed) {
                return;
            }

            if (e.shiftKey) { // if shift key pressed for shift + tab combination
                if (document.activeElement === firstFocusableElement) {
                    e.preventDefault();
                    lastFocusableElement.focus(); // add focus for the last focusable element
                }
            } else { // if tab key is pressed
                if (firstFocusableElement === lastFocusableElement) {
                    e.preventDefault();
                    firstFocusableElement.focus();
                }

                if (document.activeElement === lastFocusableElement) {
                    e.preventDefault();
                    firstFocusableElement.focus();
                }
            }
        });

        setTimeout(()=> {
            firstFocusableElement.focus();
        }, 300);
    }

    /** Drawer open */
    public open() {
        if (document.activeElement) {
            this.previousFocus = document.activeElement;
        }

        this.bodyOverlay?.openOverlay();

        document.body.classList.add('prevent-scroll');
        this.setAttribute('open', '');
        this.setAttribute('aria-hidden', 'false');
        this.handleFocusLock();
    }

    /** Drawer close */
    public close() {
        const previous = this.previousFocus as HTMLElement;

        this.bodyOverlay?.closeOverlay();

        document.body.classList.remove('prevent-scroll');
        this.removeAttribute('open');
        this.setAttribute('aria-hidden', 'true');
        previous.focus();
    }
}
